/**
 * GoogleMaps.js creates a Google Maps map in a specified container element and offers various methods to interact with it.
 *
 * Implementation example:
 * ```
 * var args = {
 *     center: {
 *         lat: 47.070714,
 *         lng: 15.439503
 *     },
 *     zoom: 10,
 *     cbScriptLoaded: function (instance) {
 *         // do something when the map is initialized
 *     }
 * };
 *
 * var myMap = new EN.GoogleMaps($('.my_map'), args);
 * ```
 *
 * @class GoogleMaps
 * @author EN GARDE (Martin Holler)
 * @version 1.5
 *
 * @param {jQuery} $target - The element the map will be placed in.
 * @param {object} options - Overrides defaults, see below.
 * @property {string} [apiKey] - Google Maps Javascript API key.
 * @property {string} [language] - The UI language code. This will be the browser's language if not set.
 * @property {number} [apiVersion=3] - The API version to use. Use with caution, future API versions might break compatibility with this class!
 * @property {object} center - An object with properties for latitude and longitude.
 * @property {number} center.lat - Latitude parameter.
 * @property {number} center.lng - Longitude parameter.
 * @property {number} [zoom=10] - Map zoom level.
 * @property {string} [mapTypeId=roadmap]: Any of the following map types: "roadmap", "satellite", "hybrid", "terrain".
 * @property {boolean} [disableDefaultUI=true]: Disables all UI controls.
 * @property {object} [styles] - Custom map styles. See https://developers.google.com/maps/documentation/javascript/styling#styling_the_default_map, Wizard: http://googlemaps.github.io/js-samples/styledmaps/wizard/index.html
 * @property {boolean} [addMarkerAtCenter=false]: Automatically adds a marker to the center of the map.
 * @property {boolean} [responsiveCenter=true]: Keeps the center of the map centered when the window size is changed.
 * @property {boolean} [zoomFix=true]: Avoids unintentional zooming of the map when the window is scrolled. Zooming is enabled only when the map is in focus (i.e. if it was already interacted with). Disables zooming and panning on touch devices altogether.
 * @property {string} [initClass=initialized]: Class to be added to the element when the map was initialized.
 * @property {function} [cbScriptLoaded]: Callback function that is called when the map was initialized.
 *
 *
 * Change Log:
 * 1.5	added libraries option
 * 1.4	fixed marker at center
 * 1.3	added addInfoWindow method
 * 1.2	added refresh method, refresh on mavMove
 * 1.1	fixed custom map styles
 * 1.0	initial version
 */

var EN = EN || {};

EN.GoogleMaps = (function ($) {
	'use strict';

	/** @constructor */
	function GoogleMaps($target, options) {
		var that = this;
		var urlParameters = {};

		that.options = $.extend({}, that.defaults, options);
		that.map = undefined;
		that.markers = [];
		that.zoomLocked = false;

		// get the Google script once
		if (!GoogleMaps.scriptLoaded) {
			GoogleMaps.scriptLoaded = true;

			// set url parameters
			urlParameters.key = that.options.apiKey;
			urlParameters.v = that.options.apiVersion;
			urlParameters.language = that.options.language;
			urlParameters.callback = 'EN.GoogleMaps.cbScriptLoaded';

			if (typeof that.options.libraries === 'object') {
				urlParameters.libraries = that.options.libraries.join(',');
			}

			$.getScript('https://maps.googleapis.com/maps/api/js?' + $.param(urlParameters));
		}

		// run init when the script is loaded
		if (!GoogleMaps.cbFired) {
			$(document).on('EN.GoogleMaps.scriptLoaded', function () {
				that.init($target, that.options);

				if (typeof that.options.cbScriptLoaded === 'function') {
					that.options.cbScriptLoaded(that);
				}
			});
		}
		else {
			that.init($target, that.options);

			if (typeof that.options.cbScriptLoaded === 'function') {
				that.options.cbScriptLoaded(that);
			}
		}
	}

	// static properties
	GoogleMaps.cbFired = false;
	GoogleMaps.scriptRequested = false;
	GoogleMaps.cbScriptLoaded = function () {
		GoogleMaps.scriptRequested = true;
		GoogleMaps.cbFired = true;
		$(document).trigger('EN.GoogleMaps.scriptLoaded');
	};

	// defaults
	GoogleMaps.prototype.defaults = {
		// url parameters
		apiKey: undefined,
		language: undefined,
		apiVersion: 3,

		// class init parameters
		center: {
			lat: undefined,
			lng: undefined
		},
		zoom: 10,
		minZoom: undefined,
		maxZoom: undefined,
		mapTypeId: 'roadmap',
		disableDefaultUI: true,
		styles: [],
		addMarkerAtCenter: false,
		responsiveCenter: true,
		zoomFix: true,
		initClass: 'en-is-initialized',
		cbScriptLoaded: undefined
	};

	/**
	 * Creates the map. Inherits parameters from the class constructor.
	 * @memberof GoogleMaps
	 * @param {jQuery} $target - The element the map will be placed in.
	 * @param {object} options - Overrides defaults, see class parameters.
	 * @return {object} map object
	 */
	GoogleMaps.prototype.init = function ($target, options) {
		// argument checks
		if (typeof google === 'undefined') {
			console.error('[GoogleMaps.js] The Google Maps Javascript API was not found.');
			return;
		}

		if (typeof $target !== 'object') {
			console.error('[GoogleMaps.js] Could not initialize Google Maps. The supplied target element is invalid.');
			return;
		}

		var that = this;

		// convert map type if necessary
		if (typeof options.mapTypeId === 'string') {
			options.mapTypeId = google.maps.MapTypeId[options.mapTypeId.toUpperCase()];
		}

		// type conversions
		options.center.lat = parseFloat(options.center.lat);
		options.center.lng = parseFloat(options.center.lng);
		options.zoom = parseInt(options.zoom, 10);
		options.minZoom = options.minZoom ? parseInt(options.minZoom, 10) : undefined;
		options.maxZoom = options.maxZoom ? parseInt(options.maxZoom, 10) : undefined;

		// create the map
		that.map = new google.maps.Map($target.get(0), options);

		// add a marker to the center
		if (options.addMarkerAtCenter) {
			that.addMarker(options.center);
		}

		// keep the center in the middle of the map
		if (options.responsiveCenter) {
			google.maps.event.addDomListener(window, 'resize', function() {
				var center = that.map.getCenter();
				google.maps.event.trigger(that.map, 'resize');
				that.map.setCenter(center);
			});
		}

		// fix unintentional zooming
		if (options.zoomFix) {
			that.lockZoom();

			if (!$('html').hasClass('touch')) {
				$(document).on('mousedown', function (e) {
					if (that.zoomLocked && $(e.target).parents('.' + that.options.initClass).length) {
						that.unlockZoom();
					}
				});

				$(window).on('scroll', function () {
					if (!that.zoomLocked) {
						that.lockZoom();
					}
				});
			}
		}

		// apply style
		if (options.style) {
			that.map.setOptions({styles: options.style});
		}
		// refresh the map after a mav move
		$(document).on('mavAfterMove', function (e, data) {
			if (data && data.direction === 'in' && $(e.target).find($target).length) {
				that.refresh();
			}
		});

		// set an initialization class
		$target.addClass(options.initClass);

		// append the instance to the target
		$target.data('EnMap', that);

		return that.map;
	};

	/**
	 * Sets the center of the map to the given coordinates.
	 * @memberof GoogleMaps
	 * @param {number} lat - Latitude value.
	 * @param {number} lng - Longitude value.
	 * @return {object} map object
	 */
	GoogleMaps.prototype.setCenter = function (lat, lng) {
		if (!lat || !lng) {
			return;
		}

		var that = this;
		var center = new google.maps.LatLng(parseFloat(lat), parseFloat(lng));

		that.map.setCenter(center);

		return that.map;
	};

	/**
	 * Sets the map's zoom level.
	 * @memberof GoogleMaps
	 * @param {number} zoomLevel
	 * @return {object} map object
	 */
	GoogleMaps.prototype.setZoom = function (zoomLevel) {
		if (typeof zoomLevel !== 'number') {
			return;
		}

		var that = this;

		that.map.setZoom(parseInt(zoomLevel, 10));

		return that.map;
	};

	/**
	 * Sets the map type.
	 * @memberof GoogleMaps
	 * @param {string} type - Any of the following map types: "roadmap", "satellite", "hybrid", "terrain".
	 * @return {object} map object
	 */
	GoogleMaps.prototype.setMapType = function (type) {
		if (typeof type !== 'string') {
			return false;
		}

		var that = this;

		that.map.setMapTypeId(google.maps.MapTypeId[type.toUpperCase()]);

		return that.map;
	};

	/**
	 * Adds a marker to the map.
	 * @memberof GoogleMaps
	 * @param {object} options - An object containing information about the marker.
	 * @param {number} options.lat - Latitude value.
	 * @param {number} options.lng - Longitude value.
	 * @param {string} [options.image] - Custom marker image URL. Can also be an SVG image.
	 * @param {string} [options.url] - URL to be called when the marker gets clicked.
	 * @return {object} marker object
	 */
	GoogleMaps.prototype.addMarker = function (options) {
		if (typeof options !== 'object' || !options.lat || !options.lng) {
			return;
		}

		var that = this;

		var marker = new google.maps.Marker({
			map: that.map,
			position: new google.maps.LatLng(parseFloat(options.lat), parseFloat(options.lng)),
			icon: options.image, // can also be svg
			label: options.label
		});

		if (typeof options.url === 'string') {
			google.maps.event.addListener(marker, 'click', function () {
				window.location.href = options.url;
			});

			marker._hasClickHandler = true;
		}

		that.markers.push(marker);

		return marker;
	};

	/**
	 * Adds multiple markers to the map.
	 * @memberof GoogleMaps
	 * @param {object[]} markers - An array of marker options.
	 * @param {object} markers.option - See addMarker() for information on how to format this object.
	 * @return {object[]} array of marker objects
	 */
	GoogleMaps.prototype.addMarkers = function (markers) {
		var that = this;

		$.each(markers, function (index, marker) {
			that.addMarker(marker);
		});

		return that.markers;
	};

	/**
	 * Moves a given marker to a new position.
	 * @memberof GoogleMaps
	 * @param {object} marker - The marker object to move.
	 * @param {number} lat - New latitude value.
	 * @param {number} lng - New longitude value.
	 * @return {object} marker object
	 */
	GoogleMaps.prototype.moveMarker = function (marker, lat, lng) {
		if (typeof marker !== 'object' || !lat || !lng) {
			return;
		}

		var that = this;

		marker.setPosition(new google.maps.LatLng(parseFloat(lat), parseFloat(lng)));

		return marker;
	};

	/**
	 * Removes a given marker from the map.
	 * @memberof GoogleMaps
	 * @param {object} marker - The marker object to remove.
	 * @return {object} marker object
	 */
	GoogleMaps.prototype.removeMarker = function (marker) {
		var that = this;
		var markerIndex = that.markers.indexOf(marker);

		marker.setMap(null);

		if (markerIndex !== -1) {
			that.markers.splice(markerIndex, 1);
		}

		return marker;
	};

	/**
	 * Returns an array of all markers currently on the map.
	 * @memberof GoogleMaps
	 * @return {object[]} array of marker objects
	 */
	GoogleMaps.prototype.getMarkers = function () {
		var that = this;

		return that.markers;
	};

	/**
	 * Disables zooming on the map using the scroll wheel.
	 * @memberof GoogleMaps
	 * @return {object} map object
	 */
	GoogleMaps.prototype.lockZoom = function () {
		var that = this;

		that.map.setOptions({
			scrollwheel: false
		});

		that.zoomLocked = true;

		return that.map;
	};

	/**
	 * Enables zooming on the map using the scroll wheel.
	 * @memberof GoogleMaps
	 * @return {object} map object
	 */
	GoogleMaps.prototype.unlockZoom = function () {
		var that = this;

		that.map.setOptions({
			scrollwheel: true
		});

		that.zoomLocked = false;

		return that.map;
	};

	/**
	 * Triggers a map redraw.
	 * @return {object} map object
	 */
	GoogleMaps.prototype.refresh = function () {
		var that = this;
		var center = that.map.getCenter();

		google.maps.event.trigger(that.map, 'resize');
		that.map.setCenter(center);

		return that.map;
	};

	/**
	 * Fits the map's bounds to the currently set markers.maxZoom
	 * @param {number} [maxZoom] - Maxium zoom value to set.
	 * @return {object} map object
	 */
	GoogleMaps.prototype.fitToMarkerBounds = function (maxZoom) {
		var that = this;
		var markers = that.getMarkers();
		var bounds = new google.maps.LatLngBounds();

		if (typeof maxZoom === 'number') {
			that.map.maxZoom = maxZoom;
		}

		$.each(markers, function (index, marker) {
			bounds.extend(marker.position);
		});

		that.map.fitBounds(bounds);

		if (typeof maxZoom === 'number') {
			google.maps.event.addListenerOnce(that.map, 'bounds_changed', function () {
				that.map.maxZoom = undefined;
			});
		}

		return that.map;
	};

	/**
	 * Adds an info window to a marker.
	 * @memberof GoogleMaps
	 * @param {object} marker - Marker to attach the info window to.
	 * @param {string} content - Content of the info window.
	 * @param {object} [options] - Info window options.
	 * @param {number} options.maxWidth - Maximum width of the info window, regardless of its content's width.
	 * @param {number} options.openByDefault - Whether or not the info window should be open by default.
	 * @return {object} info window object
	 */
	GoogleMaps.prototype.addInfoWindow = function (marker, content, options) {
		options = options || {};

		if (!marker || typeof content !== 'string') {
			return;
		}

		var that = this;
		var infoWindow;

		infoWindow = new google.maps.InfoWindow({
			content: content,
			maxWidth: options.maxWidth,
			disableAutoPan: options.openByDefault || marker._hasClickHandler
		});

		if (options.openByDefault || marker._hasClickHandler) {
			infoWindow.open(that.map, marker);
		}
		else {
			marker.addListener('click', function () {
				infoWindow.open(that.map, marker);
			});
		}

		marker._infoWindow = infoWindow;

		return infoWindow;
	};

	return GoogleMaps;
}(jQuery));
